import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { SectionLabel, CornerCard, StatusPip } from "./primitives";

/**
 * [02] Anomaly catalog. Close-reading of the four most important
 * detection kinds. Each card is a credible "what the model sees"
 * cropped tile, paired with technical metadata + the model rationale.
 */
export function AnomalyCatalog() {
  const { t } = useTranslation();
  return (
    <section id="vision" className="relative border-b border-[color:var(--color-app-edge)]">
      <div className="absolute inset-0 bg-topo pointer-events-none opacity-50" />
      <div className="relative mx-auto max-w-[1320px] px-6 lg:px-10 py-24 md:py-28">
        <SectionLabel
          index={t("catalog.section.index")}
          title={t("catalog.section.title")}
          kicker={t("catalog.section.kicker")}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          <div>
            <h2 className="text-display text-5xl md:text-6xl">
              {t("catalog.headline.before")}{" "}
              <span className="text-accent text-accent-leaf">
                {t("catalog.headline.accent")}
              </span>{" "}
              {t("catalog.headline.after")}
            </h2>
          </div>
          <div className="md:pl-8 md:border-l md:border-[color:var(--color-app-edge)] text-[color:var(--color-app-text)]/75 leading-relaxed flex items-center">
            <p>{t("catalog.intro")}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <AnomalyTileCard
            tint="amber"
            label={t("catalog.tiles.stress.label")}
            sev="S3"
            conf={0.91}
            chip={<StatusPip color="amber" pulse>S3 · 0.91</StatusPip>}
            description={t("catalog.tiles.stress.summary")}
          >
            <TileStress />
          </AnomalyTileCard>

          <AnomalyTileCard
            tint="water"
            label={t("catalog.tiles.irrigation.label")}
            sev="S2"
            conf={0.84}
            chip={<StatusPip color="water" pulse>S2 · 0.84</StatusPip>}
            description={t("catalog.tiles.irrigation.summary")}
          >
            <TileIrrigation />
          </AnomalyTileCard>

          <AnomalyTileCard
            tint="coral"
            label={t("catalog.tiles.pest.label")}
            sev="S4"
            conf={0.78}
            chip={<StatusPip color="coral" pulse>S4 · 0.78</StatusPip>}
            description={t("catalog.tiles.pest.summary")}
          >
            <TilePest />
          </AnomalyTileCard>

          <AnomalyTileCard
            tint="saffron"
            label={t("catalog.tiles.missing.label")}
            sev="S2"
            conf={0.93}
            chip={<StatusPip color="saffron">S2 · 0.93</StatusPip>}
            description={t("catalog.tiles.missing.summary")}
          >
            <TileMissingRows />
          </AnomalyTileCard>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-px bg-[color:var(--color-app-edge)] border border-[color:var(--color-app-edge)]">
          <FactCell label={t("catalog.facts.polygon.label")}    value={t("catalog.facts.polygon.value")} />
          <FactCell label={t("catalog.facts.severity.label")}   value={t("catalog.facts.severity.value")} />
          <FactCell label={t("catalog.facts.confidence.label")} value={t("catalog.facts.confidence.value")} />
        </div>
      </div>
    </section>
  );
}

function AnomalyTileCard({
  label,
  sev,
  conf,
  chip,
  description,
  tint,
  children,
}: {
  label: string;
  sev: string;
  conf: number;
  chip: ReactNode;
  description: string;
  tint?: "amber" | "water" | "coral" | "saffron";
  children: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <CornerCard tint={tint}>
      <div className="flex items-center justify-between px-3 py-2 border-b border-[color:var(--color-app-edge)] coord-mono">
        <span className="opacity-70">{label.toUpperCase().replace(/ /g, "_")}</span>
        <span className="opacity-50">{t("catalog.gsdLabel")}</span>
      </div>
      <div className="relative aspect-square overflow-hidden">
        {children}
        <div className="absolute top-2 left-2">{chip}</div>
        <div className="absolute bottom-2 right-2 coord-mono opacity-70">
          {sev} · {conf.toFixed(2)}
        </div>
      </div>
      <div className="px-4 py-4 border-t border-[color:var(--color-app-edge)]">
        <h3 className="font-display text-xl mb-1.5">
          {label}
        </h3>
        <p className="text-sm text-[color:var(--color-app-text)]/70 leading-relaxed">{description}</p>
      </div>
    </CornerCard>
  );
}

function FactCell({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-[color:var(--color-app-bg)] px-5 py-5">
      <div className="label-mono opacity-60 mb-1.5">{label}</div>
      <div className="font-mono text-sm num">{value}</div>
    </div>
  );
}

/* ── Per-anomaly synthetic tiles ─────────────────────────────────── */

function TileStress() {
  return (
    <svg viewBox="0 0 400 400" className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
      <defs>
        <pattern id="stress-rows" patternUnits="userSpaceOnUse" width="6" height="400" patternTransform="rotate(-8)">
          <line x1="0" y1="0" x2="0" y2="400" stroke="rgba(20,30,10,0.22)" strokeWidth="1" />
        </pattern>
        <linearGradient id="stress-canopy" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#5a8543" />
          <stop offset="100%" stopColor="#3d5c2d" />
        </linearGradient>
        <linearGradient id="stress-band" x1="0" x2="1" y1="0.4" y2="0.6">
          <stop offset="0%" stopColor="rgba(180, 110, 50, 0)" />
          <stop offset="30%" stopColor="rgba(180, 110, 50, 0.6)" />
          <stop offset="70%" stopColor="rgba(180, 110, 50, 0.6)" />
          <stop offset="100%" stopColor="rgba(180, 110, 50, 0)" />
        </linearGradient>
      </defs>
      <rect x="0" y="0" width="400" height="400" fill="url(#stress-canopy)" />
      <rect x="0" y="0" width="400" height="400" fill="url(#stress-rows)" />
      <rect x="0" y="140" width="400" height="120" fill="url(#stress-band)" />
      <polygon
        points="40,170 360,150 370,250 50,260"
        fill="rgba(217, 119, 6, 0.18)"
        stroke="#fbbf24"
        strokeWidth="2"
      />
    </svg>
  );
}

function TileIrrigation() {
  return (
    <svg viewBox="0 0 400 400" className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
      <defs>
        <radialGradient id="pivot-base" cx="0.5" cy="0.5" r="0.55">
          <stop offset="0%" stopColor="#5a8543" />
          <stop offset="100%" stopColor="#3d5c2d" />
        </radialGradient>
        <radialGradient id="pivot-dry" cx="0.5" cy="0.5" r="0.55">
          <stop offset="0%" stopColor="rgba(170, 130, 70, 0)" />
          <stop offset="60%" stopColor="rgba(170, 130, 70, 0)" />
          <stop offset="100%" stopColor="rgba(170, 130, 70, 0.7)" />
        </radialGradient>
        <pattern id="pivot-rows" patternUnits="userSpaceOnUse" width="240" height="240" patternTransform="rotate(0)" x="200" y="200">
          <circle cx="0" cy="0" r="20"  fill="none" stroke="rgba(20,30,10,0.15)" strokeWidth="1" />
          <circle cx="0" cy="0" r="50"  fill="none" stroke="rgba(20,30,10,0.15)" strokeWidth="1" />
          <circle cx="0" cy="0" r="80"  fill="none" stroke="rgba(20,30,10,0.15)" strokeWidth="1" />
          <circle cx="0" cy="0" r="110" fill="none" stroke="rgba(20,30,10,0.15)" strokeWidth="1" />
          <circle cx="0" cy="0" r="140" fill="none" stroke="rgba(20,30,10,0.15)" strokeWidth="1" />
          <circle cx="0" cy="0" r="170" fill="none" stroke="rgba(20,30,10,0.15)" strokeWidth="1" />
        </pattern>
      </defs>
      <rect x="0" y="0" width="400" height="400" fill="#1e2a14" />
      <circle cx="200" cy="200" r="180" fill="url(#pivot-base)" />
      <rect x="0" y="0" width="400" height="400" fill="url(#pivot-rows)" />
      {/* Dry wedge */}
      <path d="M 200 200 L 360 90 A 180 180 0 0 1 380 240 Z" fill="rgba(170, 130, 70, 0.55)" />
      <path d="M 200 200 L 360 90 A 180 180 0 0 1 380 240 Z" fill="none" stroke="#5fb3c4" strokeWidth="2" />
      <circle cx="200" cy="200" r="180" fill="none" stroke="rgba(20,30,10,0.35)" strokeWidth="1" />
      {/* Pivot mast dot */}
      <circle cx="200" cy="200" r="3" fill="#0a0d0a" />
    </svg>
  );
}

function TilePest() {
  return (
    <svg viewBox="0 0 400 400" className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
      <defs>
        <linearGradient id="pest-canopy" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#5a8543" />
          <stop offset="100%" stopColor="#3d5c2d" />
        </linearGradient>
        <pattern id="pest-rows" patternUnits="userSpaceOnUse" width="7" height="400" patternTransform="rotate(86)">
          <line x1="0" y1="0" x2="0" y2="400" stroke="rgba(20,30,10,0.22)" strokeWidth="1" />
        </pattern>
        <radialGradient id="pest-blot" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0%" stopColor="rgba(40, 20, 5, 0.85)" />
          <stop offset="70%" stopColor="rgba(40, 20, 5, 0.45)" />
          <stop offset="100%" stopColor="rgba(40, 20, 5, 0)" />
        </radialGradient>
      </defs>
      <rect x="0" y="0" width="400" height="400" fill="url(#pest-canopy)" />
      <rect x="0" y="0" width="400" height="400" fill="url(#pest-rows)" />
      {/* Dark patches */}
      {[
        { cx: 130, cy: 150, rx: 50, ry: 35 },
        { cx: 180, cy: 200, rx: 30, ry: 22 },
        { cx: 240, cy: 175, rx: 35, ry: 28 },
        { cx: 95,  cy: 220, rx: 22, ry: 18 },
        { cx: 285, cy: 230, rx: 18, ry: 14 },
      ].map((p, i) => (
        <ellipse key={i} cx={p.cx} cy={p.cy} rx={p.rx} ry={p.ry} fill="url(#pest-blot)" />
      ))}
      <polygon
        points="70,130 280,120 310,250 80,260"
        fill="rgba(181, 74, 42, 0.16)"
        stroke="#ef6f43"
        strokeWidth="2"
      />
    </svg>
  );
}

function TileMissingRows() {
  return (
    <svg viewBox="0 0 400 400" className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
      <defs>
        <linearGradient id="rows-base" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#7ba34d" />
          <stop offset="100%" stopColor="#557735" />
        </linearGradient>
        <linearGradient id="rows-soil" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#5e4624" />
          <stop offset="100%" stopColor="#3a2c17" />
        </linearGradient>
      </defs>
      <rect x="0" y="0" width="400" height="400" fill="url(#rows-soil)" />
      {/* Crop rows — green stripes */}
      {Array.from({ length: 32 }).map((_, i) => {
        const x = i * 12 + 8;
        const missing = i === 12 || i === 14 || i === 22;
        return (
          <rect
            key={i}
            x={x}
            y={0}
            width="6"
            height="400"
            fill={missing ? "transparent" : "url(#rows-base)"}
            opacity={missing ? 0 : 0.95}
          />
        );
      })}
      {/* Highlight polygon over the gap */}
      <polygon
        points="140,40 200,40 200,360 140,360"
        fill="rgba(202, 138, 4, 0.15)"
        stroke="#facc15"
        strokeWidth="2"
      />
      {/* Subtle perpendicular drive-line */}
      <line x1="0" y1="200" x2="400" y2="195" stroke="rgba(0,0,0,0.25)" strokeWidth="2" />
    </svg>
  );
}
